import { isLoaderActionError } from '@findigs/helpers/src/helpers/loader-action-error';
import { useIsDevUser } from '@findigs/helpers/src/hooks/use-is-dev-user';
import { Button } from '@findigs/ui-kit/src/components/button';
import {
  Links,
  isRouteErrorResponse,
  useAsyncError,
  useRouteError,
} from '@remix-run/react';
import {
  captureException,
  captureRemixErrorBoundaryError,
} from '@sentry/remix';
import { DevErrorView } from '~/components/dev-error-view';
import { RouteErrorView } from '~/components/route-error-view';
import type { LoaderActionError, RouteError } from '~/types';

/**
 * Base error boundary that can be exported inside nested routes
 * This will render the error UI inside the parent's <Outlet />
 *
 * @returns JSX for the Error UI
 */
export function ErrorBoundary() {
  const devUser = useIsDevUser();

  const error = useRouteError();
  const asyncError = useAsyncError();

  if (error) captureRemixErrorBoundaryError(error);
  if (asyncError) captureException(asyncError);

  // Overlay with stack trace to help developers debug
  const dev = devUser ? (
    <DevErrorView error={error ?? asyncError} className="pt-12" />
  ) : null;

  if (error && (isRouteErrorResponse(error) || isLoaderActionError(error))) {
    return (
      <div className="col-4 sm:col-8 sm:start-3 md:start-5 lg:start-9 pb-16">
        <RouteErrorView error={error as RouteError | LoaderActionError} />
        {dev}
      </div>
    );
  } else {
    return (
      <>
        <Links />
        <div className="col-4 sm:col-8 sm:start-3 md:start-5 lg:start-9 pb-16">
          <h1 className="text-d-sm sm:text-d-md md:text-d-md mb-6">
            Error occurred
          </h1>
          <p className="mb-12 md:text-b-lg-regular">
            Something went wrong, please reach out to{' '}
            <a href="mailto:pm-support@findigs.com">pm-support@findigs.com</a>{' '}
            or try again.
          </p>
          <Button as="a" href="/" variant="Tonal">
            Back to home
          </Button>
          {dev}
        </div>
      </>
    );
  }
}

/**
 * Base error boundary that can be exported inside nested routes
 * This will render the error UI inside the parent's <Outlet />
 *
 * **Meant to be used where the parent does NOT include a `<main>` element**
 *
 * @returns JSX for the Error UI
 */
export function ErrorBoundaryWithMain() {
  return (
    <main className="grid">
      <ErrorBoundary />
    </main>
  );
}
